import { ApolloClient, ApolloLink, from, InMemoryCache, type NormalizedCacheObject, HttpLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';
import { extractFiles } from 'extract-files';
import { createClient as createWsClient } from 'graphql-ws';
import { type i18n as I18n } from 'i18next';
import { isEmpty, isNil } from 'lodash/fp';
import PubSub from 'pubsub-js';
import introspection from './api/introspection';
import prepareForGraphQL from './utilities/prepareForGraphQL';
import validateTokenData from './utilities/validateTokenData';

const createWsLink = (getContext: GetContext) => {
    // track if a restart is requested
    let restartRequested = false;

    // restart handler
    let restart = () => {
        restartRequested = true;
    };

    // restart whenever the token changed
    PubSub.subscribe('core.tokenUpdate', () => restart());

    const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';

    // create ws link
    return new GraphQLWsLink(
        createWsClient({
            url: `${scheme}://${window.location.host}/graphql`,
            lazy: true,

            // provide authorization header in connection params
            connectionParams: () => {
                const { token } = getContext();

                if (token) {
                    return { authorization: token };
                }

                return {};
            },

            on: {
                // we usually need to restart whenever the token changed
                // so we need graceful restarts
                opened: socket => {
                    restart = () => {
                        // @ts-ignore
                        if (socket.readyState === WebSocket.OPEN) {
                            // if the socket is still open for the restart, do the restart
                            // @ts-ignore
                            socket.close(4205, 'Client Restart');
                        } else {
                            // otherwise the socket might've closed, indicate that you want
                            // a restart on the next opened event
                            restartRequested = true;
                        }
                    };

                    // just in case you were eager to restart
                    if (restartRequested) {
                        restartRequested = false;
                        restart();
                    }
                },
            },
        })
    );
};

const getCache = () => {
    const cache = new InMemoryCache({
        possibleTypes: introspection.possibleTypes,
        typePolicies: {
            /**
             * to avoid value of `isAgreed` in `guarantorAgreements`
             * to override the value of `isAgreed` in `applicationAgreement`
             */
            ApplicationAgreement: { keyFields: ['id', 'isAgreed'] },
        },
    });
    const initialStateElement = document.querySelector('script[data-role="runtime-config"]');

    if (!initialStateElement) {
        return cache;
    }

    const config = initialStateElement.textContent;

    if (!config) {
        throw new Error('Runtime config missing');
    }

    // restore from state
    cache.restore(JSON.parse(config));

    return cache;
};

export type GetContext = () => { i18n?: I18n; token?: string };

const OPERATIONS_WITHOUT_TOKEN = new Set(['getRouterContext']);
const shouldExcludeToken = (token: string, operation: string) => {
    if (isEmpty(token)) {
        return true;
    }

    return OPERATIONS_WITHOUT_TOKEN.has(operation) && isNil(validateTokenData(token));
};

const createApolloClient = (getContext: GetContext): ApolloClient<NormalizedCacheObject> => {
    // push the JWT token in headers
    const authLink = new ApolloLink((operation, forward) => {
        operation.setContext(({ headers }) => {
            const { i18n, token } = getContext();
            const customHeaders = { ...headers, 'Accept-Language': i18n?.language };

            if (!shouldExcludeToken(token, operation.operationName)) {
                customHeaders.Authorization = `Bearer ${token}`;
            }

            return { headers: customHeaders };
        });

        return forward(operation);
    });

    // if there's file we are going to use the upload link
    // otherwise use the batch http link
    const httpLink = ApolloLink.split(
        operation => extractFiles(operation).files.size > 0,
        createUploadLink({ uri: '/graphql', credentials: 'include' }),
        new HttpLink({ uri: '/graphql', credentials: 'include' })
    );

    // websocket link
    const wsLink = createWsLink(getContext);

    // split between HTTP and WS protocols
    const rootLink = ApolloLink.split(
        ({ query }) => {
            const definition = getMainDefinition(query);

            return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
        },
        wsLink,
        from([authLink, httpLink])
    );

    // on not authenticated listener
    const disconnectLink = onError(({ graphQLErrors }) => {
        if (graphQLErrors) {
            const isUnauthenticated = graphQLErrors.some(error => error.extensions.code === 'UNAUTHENTICATED');

            if (isUnauthenticated) {
                PubSub.publish('core.unauthenticated');
            }
        }
    });

    // link to clean data
    const cleanLink = new ApolloLink((operation, forward) => {
        // eslint-disable-next-line no-param-reassign
        operation.variables = prepareForGraphQL(operation.variables);

        return forward(operation);
    });

    return new ApolloClient({
        ssrMode: false,
        link: from([cleanLink, disconnectLink, rootLink]),
        cache: getCache(),
    });
};

export default createApolloClient;
